import { computed, reactive, ref } from "vue";

import EventBus from "@/utils/eventBus";

import { useBaseComposable } from "./useBaseComposable";

import { UserService } from "@/services";

import { isLoading } from "@/shared/base.service";

import { useAuthStore } from "@/stores/auth.store";

import type { User } from "@/types/model/user";
import type { CreateUserRequest } from "@/types/request/createUser";
import type { UpdateUserRequest } from "@/types/request/updateUser";

export function useUser() {
  const { createValidationRules, required, minLength, email, sameAs } =
    useBaseComposable();

  const user = ref<User>();
  const users = ref<User[]>([]);

  const columns = ref<object[]>([
/*     {
      title: "Nome",
      align: "left",
      key: "name",
      value: (item: User) => item.name ?? "Nome não informado",
    }, */
    { title: "Email", align: "center", key: "login" },
    {
      title: "Tipo",
      align: "center",
      key: "type",
      value: (item: User) => (item.admin == true ? "Administrador" : "Usuário"),
    },
    {
      title: "Status",
      align: "center",
      key: "status",
      value: (item: User) => item.active,
    },
    {
      title: "Ações",
      align: "center",
      key: "actions",
      sortable: false,
      value: (item: User) => item,
      width: "200px",
    },
  ]);

  const pagination = ref({ page: 1, size: 10 });

  const newUserFormRules = createValidationRules({
    login: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      email: {
        validator: email,
        messageKey: "validation.email",
      },
    },
    password: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      minLength: {
        validator: minLength(6),
        messageKey: "validation.minLength",
        params: { min: 6 },
      },
    },
    confirmationPassword: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      minLength: {
        validator: minLength(6),
        messageKey: "validation.minLength",
        params: { min: 6 },
      },
      sameAsPassword: {
        validator: sameAs(computed(() => newUserForm.password)),
        messageKey: "validation.sameAsPassword",
      },
    },
    admin: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
    },
  });

  const newUserForm = reactive<CreateUserRequest>({
    login: "",
    password: "",
    confirmationPassword: "",
    permissions: [],
    admin: undefined,
  });

  const updateUserFormRules = createValidationRules({
    login: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      email: {
        validator: email,
        messageKey: "validation.email",
      },
    },
    user_id: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
    },
  });

  const updateUserForm = reactive<UpdateUserRequest>({
    user_id: "",
    login: "",
  });

  async function getAllUsers(page: number, size: number) {
    try {
      const response = await UserService.GetAllUsers(page, size);

      if (response) {
        users.value = response.data;

        pagination.value = {
          size: response.total_items,
          page: response.total_pages,
        };
      }

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  // Cache e controle de requisições para getUserData
  let userDataPromise: Promise<{ data: User } | null> | null = null;
  let lastFetchTime = 0;
  const CACHE_DURATION = 5000; // 5 segundos de cache

  async function getUserData(forceRefresh = false) {
    try {
      const now = Date.now();
      const authStore = useAuthStore();
      
      // Se já tem dados no store e não passou o tempo de cache, retorna do cache
      if (!forceRefresh && authStore.getUser && (now - lastFetchTime < CACHE_DURATION)) {
        return { data: authStore.getUser };
      }

      // Se já existe uma requisição em andamento, retorna a mesma promise
      if (userDataPromise) {
        return await userDataPromise;
      }

      // Cria nova requisição
      userDataPromise = UserService.GetUserData();
      
      const response = await userDataPromise;
      
      if (response) {
        authStore.setUser(response.data);
        user.value = response.data;
        lastFetchTime = now;
      }

      return response;
    } catch (error) {
      console.error(error);
    } finally {
      // Limpa a promise após completar
      userDataPromise = null;
    }
  }

  async function getUserById(user_id: string) {
    try {
      const response = await UserService.GetUserById(user_id);

      if (response && Array.isArray(response.data)) {
        user.value = response.data[0];
      } else if (response) {
        user.value = response.data;
      }

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function mfaActivate() {
    try {
      const response = await UserService.MfaActivate();

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function resetMfa(user_id: string) {
    try {
      const response = await UserService.ResetMfa(user_id);

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function createUser(payload: object) {
    try {
      const response = await UserService.CreateUser(payload);

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function updateUser(payload: UpdateUserRequest) {
    try {
      const response = await UserService.UpdateUser(payload);

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function deleteUser(payload: object) {
    try {
      const response = await UserService.DeleteUser(payload);

      if (response) EventBus.emit("user-update");

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  return {
    isLoading,
    columns,
    pagination,
    user,
    users,
    newUserFormRules,
    newUserForm,
    updateUserFormRules,
    updateUserForm,
    getAllUsers,
    getUserData,
    getUserById,
    createUser,
    updateUser,
    deleteUser,
    mfaActivate,
    resetMfa,
  };
}
